import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UIState {
  sidebarCollapsed: boolean;
  theme: 'dark' | 'light';
  language: string;
  
  // Support view state
  supportViewActive: boolean;
  supportViewUserId: string | null;
  supportViewReason: string | null;
  supportViewExpiry: string | null;
  
  // Actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTheme: (theme: 'dark' | 'light') => void;
  toggleTheme: () => void;
  setLanguage: (lang: string) => void;
  
  // Support view actions
  enterSupportView: (userId: string, reason: string) => void;
  exitSupportView: () => void;
  isSupportViewExpired: () => boolean;
  
  // Initialize theme on app load
  initializeTheme: () => void;

  mobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;
}

const applyTheme = (theme: 'dark' | 'light') => {
  const root = document.documentElement;
  
  // Update class
  root.classList.remove('dark', 'light');
  root.classList.add(theme);
  
  // Update background-color inline style
  root.style.backgroundColor = theme === 'dark' ? '#020617' : '#f8fafc';
  
  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', theme === 'dark' ? '#020617' : '#f8fafc');
  }
};

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      theme: 'dark',
      language: 'en',
      
      supportViewActive: false,
      supportViewUserId: null,
      supportViewReason: null,
      supportViewExpiry: null,

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setSidebarCollapsed: (collapsed: boolean) => {
        set({ sidebarCollapsed: collapsed });
      },

      setTheme: (theme: 'dark' | 'light') => {
        applyTheme(theme);
        set({ theme });
      },

      toggleTheme: () => {
        const { theme } = get();
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        applyTheme(newTheme);
        set({ theme: newTheme });
      },

      setLanguage: (lang: string) => {
        set({ language: lang });
      },

      enterSupportView: (userId: string, reason: string) => {
        // Support view expires after 10 minutes
        const expiry = new Date(Date.now() + 10 * 60 * 1000).toISOString();
        set({
          supportViewActive: true,
          supportViewUserId: userId,
          supportViewReason: reason,
          supportViewExpiry: expiry,
        });
      },

      exitSupportView: () => {
        set({
          supportViewActive: false,
          supportViewUserId: null,
          supportViewReason: null,
          supportViewExpiry: null,
        });
      },

      isSupportViewExpired: () => {
        const { supportViewExpiry } = get();
        if (!supportViewExpiry) return true;
        return new Date(supportViewExpiry) < new Date();
      },

      initializeTheme: () => {
        const { theme } = get();
        applyTheme(theme);
      },
      mobileMenuOpen: false,
      toggleMobileMenu: () => set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),
      closeMobileMenu: () => set({ mobileMenuOpen: false }),
    }),
    {
      name: 'pv-admin-ui',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        language: state.language,
      }),
      onRehydrateStorage: () => (state) => {
        // Apply theme after state is rehydrated
        if (state) {
          applyTheme(state.theme);
        }
      },
    }
  )
);
